import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { KEYS } from 'helpers/react-query.config';
import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';

interface Props {
  serviceTypeId?: number;
  clientId?: number;
  requiresServiceAndClient?: boolean;
  fromTicketFlow?: boolean;
}

/**
 * Hook para obtener la lista de estados de un cliente y tipo de servicio.
 *
 * Realiza una consulta a la API para traer los estados disponibles para un cliente y tipo de servicio.
 * Maneja errores según la respuesta del servidor.
 *
 * @param {object} [params] - Parámetros de configuración.
 * @param {number} [params.serviceTypeId] - ID del tipo de servicio.
 * @param {number} [params.clientId] - ID del cliente.
 * @param {boolean} [params.requiresServiceAndClient] - Si requiere ambos IDs para habilitar la consulta.
 * @param {boolean} [params.fromTicketFlow] - Si la consulta es desde el flujo de tickets.
 * @returns {object} Objeto con la lista de estados y el estado de carga.
 * @example
 * const { statusList, isStatusListLoading } = useGetClientStatusList({ serviceTypeId: 1, clientId: 2 });
 */
function useGetClientStatusList({
  serviceTypeId = null,
  clientId = null,
  requiresServiceAndClient = false,
  fromTicketFlow = false
}: Props = {}) {
  const { showApiError } = useGlobalContext();
  const { catalog: catalogApi } = useApiContext();
  const { t } = useTranslation('global');

  const { data: statusList, isLoading } = useQuery(
    [
      KEYS.catalog.status.getAllStatus(),
      serviceTypeId,
      clientId,
      fromTicketFlow
    ],
    () =>
      catalogApi.status.GetAllStatus({
        onlyEnabled: true,
        serviceTypeId,
        clientId,
        fromTicketFlow
      }),
    {
      refetchOnWindowFocus: false,
      select: ({ data: { data } }) => data,
      enabled: requiresServiceAndClient ? !!serviceTypeId && !!clientId : true,
      onError: (error) => showApiError(error, t('errorMessages.status'))
    }
  );

  return {
    statusList: statusList ?? [],
    isStatusListLoading: isLoading
  };
}

export default useGetClientStatusList;
